import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Spinner } from "react-bootstrap";
import TabellaLista from '../../../../components/Tabella/TabellaLista';
import { ChipFilter, filtroMultiplo } from '../../../../components/Tabella/ChipFilter';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { sports, bookingSubscriptionExpirationType } from "../../../../components/Util/enum";
import DEFAULT_STRUTTURA from "../../../../../images/DEFAULT_STRUTTURA.png";

// Valori dei filtri a chip, come compaiono nella lista. Le bozze non arrivano in elenco.
const DURATA_OPTIONS = ['Mensile', 'Trimestrale', 'Semestrale', 'Annuale', 'Fine anno solare', 'Data fissa'];
const STATO_OPTIONS = ['Attivo', 'Non attivo'];

export const AdminSubscriptionsTable = () => {
	const navigate = useNavigate();

	const [data_json, setDataJson] = useState(null);
	// Chip a scelta multipla: all'inizio tutti accesi
	const [durataFilter, setDurataFilter] = useState(DURATA_OPTIONS);
	const [statoFilter, setStatoFilter] = useState(STATO_OPTIONS);

	const filteredData = useMemo(
		() =>
			data_json &&
			data_json.filter(
				(row) =>
					filtroMultiplo(durataFilter, DURATA_OPTIONS, row.expiration_type) &&
					filtroMultiplo(statoFilter, STATO_OPTIONS, row.status_label?.label),
			),
		[data_json, durataFilter, statoFilter],
	);

	const [loadingShowed, setLoadingShowed] = useState(false);
	
	const data_column = [
		{
			Header: 'Struttura',
			accessor: 'clubName',
			Cell: ({ row }) => {
				return (
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<img
							src={row.original.imageUrlClub ? row.original.imageUrlClub : DEFAULT_STRUTTURA}
							alt={row.original.clubName}
							style={{
								width: '40px',
								height: '40px',
								borderRadius: '50%',
								marginRight: '10px'
							}}
						/>
						<div>
							{row.original.clubName}
						</div>
					</div>
				)
			}
		},
		{
			Header: 'Nome',
			Footer: 'Nome',
			accessor: 'name',
		},
		{
			Header: 'Sport',
			Footer: 'Sport',
			accessor: 'sportType',
		},
		{
			Header: 'Durata',
			Footer: 'Durata',
			accessor: 'expiration_type',
			disableGlobalFilter: true,
		},
		{
			Header: 'Prezzo base',
			Footer: 'Prezzo base',
			accessor: 'standard_price',
			disableGlobalFilter: true,
		},
		{
			Header: 'N. Acquisti',
			Footer: 'N. Acquisti',
			accessor: 'purchasesCount',
			disableGlobalFilter: true,
			Cell: ({ value }) => {
				return value || 0;
			},
			disableFilters: true,
		},
		{
			Header: 'Stato',
			Footer: 'Stato',
			accessor: 'status_label',
			disableGlobalFilter: true,
			Cell: ({ value }) => {
				return (
					<div className="d-flex align-items-center">
						<i className={`fa fa-circle me-1 ${value?value.class:''}`}></i>
						{value?value.label:''}
					</div>
				)
			},
			disableFilters: true,
		},
	];

	function openDetail(row) {
		navigate('/superadmin/booking/subscriptions/'+row.id);
	}
	
	useEffect(() => {
        const fetchData = async () => {
            try {
                const tokenDetailsString = sessionStorage.getItem('userDetails');
                if (tokenDetailsString) {
                    const tokenDetails = JSON.parse(tokenDetailsString);
                    let token = tokenDetails.idToken;
                    const apiClient = new MyApiClient();

					setLoadingShowed(true);
					const [bookingSubscriptions] = await Promise.all([
                        apiClient.getBookingSubscriptions(token)
                    ]);
					setLoadingShowed(false);

					if(bookingSubscriptions) {
						let data = [];
						bookingSubscriptions.forEach(element => {

							let status = null;
							if(element.status === 'DRAFT') {
								status = {
									"label": "Bozza",
									"class": "text-warning"
								};
							} else if(element.status === 'PURCHASABLE') {
								status = {
									"label": "Attivo",
									"class": "text-success"
								};
							} else if(element.status === 'CANCELLED') {
								status = {
									"label": "Non attivo",
									"class": "text-primary"
								};
							}

							let imageClub = null;
                            if(element.logo) {
								imageClub = element.logo;
							}

							let sportLabel = '';
							sports.forEach(element2 => {
								if(element2.value === element.sportType) {
									sportLabel = element2.label;
								}
							});

							let expiration_typeLabel = '';
							bookingSubscriptionExpirationType.forEach(element2 => {
								if(element2.value === element.expiration_type) {
									expiration_typeLabel = element2.label;
								}
							});

							let row = {
								"id": element.id,
								"name": element.name,
								"sportType": sportLabel,
								"expiration_type": expiration_typeLabel,
								"standard_price": "€ "+(element.standard_price).toFixed(2),
								"status": element.status,
								"status_label": status,
								"imageUrlClub": imageClub,
								"clubName": element.clubName,
								"purchasesCount": element.purchasesCount,
							};

							if(element.status !== 'DRAFT') {
								data.push(row);
							}
						});
						setDataJson(data);
					}
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();

    }, []);

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
				<Spinner animation="border" role="status">
					<span className="sr-only">Loading...</span>
				</Spinner>
				<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<TabellaLista
				pageList={true}
				cardTitle={'Abbonamenti'}
				hasSearch={true}
				searchPlaceholder={'Cerca per struttura, nome o sport'}
				hasFilter={false}
				filterControl={
					<div className="sid-chip-filters">
						<ChipFilter label="Durata" options={DURATA_OPTIONS} value={durataFilter} onChange={setDurataFilter} multiple />
						<ChipFilter label="Stato" options={STATO_OPTIONS} value={statoFilter} onChange={setStatoFilter} multiple />
					</div>
				}
				data_column={data_column}
				openColumn={'name'}
				openRow={openDetail}
				canOpenRow={(row) => row.status !== 'DRAFT'}
				data_json={filteredData}
				sort_by={'name'}
                sort_type={'asc'}
				modalCallBack={() => setModalCentered(true)}
			/>
		</>
	)
}
export default AdminSubscriptionsTable;